// src/components/IconDemo.tsx
import React, { useEffect, useMemo, useState } from 'react';
import Fuse from 'fuse.js';
import {
  Box,
  Grid,
  RadioGroup,
  Radio,
  TextField,
  Select,
  MenuItem,
  Typography,
  Button,
  Alert,
  Tooltip,
  Stack,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  InputLabel,
  InputAdornment,
} from '@mui/material';
import { getIcons } from './iconLoader';
import { Icon } from './types';
import useClipboard from 'react-use-clipboard';
import { useDebouncedCallback } from 'use-debounce';

type IconButtonProps = {
  icon: Icon;
  onCopy: () => void;
};

const IconButton: React.FC<IconButtonProps> = ({ icon, onCopy }) => {
  const path = `import ${icon.name} from '${icon.path}';`;

  const [, copy] = useClipboard(path);

  const copyIcon = (icon: Icon) => {
    copy();
    onCopy();
  };

  return (
    <Button
      onClick={() => copyIcon(icon)}
      variant="text"
      startIcon={<img src={icon.iconPath} alt={icon.name} />}
      sx={{ textTransform: 'none' }}
    >
      {icon.name}
    </Button>
  );
};

const PAGE_SIZE = 40;

const IconDemo: React.FC = () => {
  const [view, setView] = useState<'Line' | 'Solid'>('Line');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string>('All');
  const [isCopied, setCopied] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const icons = getIcons(view);
  const [filteredResults, setFilteredResults] = useState<Icon[]>(icons);

  const fuse = useMemo(() => {
    return new Fuse(icons, {
      keys: ['name'],
      threshold: 0.2,
    });
  }, [icons]);

  const handleSearch = useDebouncedCallback((value: string) => {
    let results = icons;
    if (value.trim() !== '') {
      results = fuse.search(value).map((result) => result.item);
    }
    if (category !== 'All') {
      results = results.filter((icon) => icon.category === category);
    }
    setFilteredResults(results);
    setCurrentPage(1);
  }, 300);

  const handleCategoryChange = (value: string) => {
    setCategory(value);
    let results = icons;
    if (search.trim() !== '') {
      results = fuse.search(search).map((result) => result.item);
    }
    if (value !== 'All') {
      results = results.filter((icon) => icon.category === value);
    }
    setFilteredResults(results);
    setCurrentPage(1);
  };

  const categories = useMemo(() => ['All', ...Array.from(new Set(icons.map((icon) => icon.category)))], [icons]);

  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const handleClearSearch = () => {
    setSearch('');
    handleSearch('');
    setCurrentPage(1);
  };

  useEffect(() => {
    handleSearch(search);
  }, [view, category, search, handleSearch]);

  const paginatedResults = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return filteredResults.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredResults, currentPage]);

  const totalPages = Math.ceil(filteredResults.length / PAGE_SIZE);

  return (
    <Stack padding={2} spacing={2} alignItems="center" justifyContent="center">
      <Card sx={{ width: '100%', maxWidth: 'md' }}>
        <CardContent>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <TextField
              label="Icon search"
              placeholder="Search for an icon"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                handleSearch(e.target.value);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <span role="img" aria-label="search">
                      🔍
                    </span>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Clear search">
                      <Button onClick={handleClearSearch}>X</Button>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Icon category</InputLabel>
            <Select
              value={category}
              label="Icon category"
              onChange={(event) => handleCategoryChange(event.target.value)}
            >
              {categories.map((category) => (
                <MenuItem value={category} key={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Icon family
            </Typography>
            <RadioGroup
              name="view"
              value={view}
              onChange={(e) => setView(e.target.value as 'Line' | 'Solid')}
              row
            >
              <FormControlLabel value="Line" control={<Radio />} label="Line" />
              <FormControlLabel value="Solid" control={<Radio />} label="Solid" />
            </RadioGroup>
          </FormControl>
        </CardContent>
      </Card>
      <Stack spacing={3} maxWidth="lg" width="100%">
        <Typography variant="h5">Click an icon to copy its path</Typography>
        {isCopied && (
          <Box position="fixed" bottom={0} right={0} zIndex={1000} padding="8px" display="flex">
            <Alert severity="success" action={<Button onClick={() => setCopied(false)}>Close</Button>}>
              Copied to clipboard!
            </Alert>
          </Box>
        )}
        <Grid container spacing={2}>
          {paginatedResults.map((icon) => (
            <Grid item xs={3} key={icon.name}>
              <IconButton icon={icon} onCopy={handleCopy} />
            </Grid>
          ))}
        </Grid>

        <Stack direction="row" justifyContent="center" alignItems="center" sx={{ mt: '16px' }}>
          <Button
            variant="contained"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <Typography sx={{ mx: '16px' }}>
            Page {currentPage} of {totalPages}
          </Typography>
          <Button
            variant="contained"
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default IconDemo;
